import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import AxiosInstance from "../../../shared/utils/axios";
import { useAuth } from "../../../lib/auth/AuthContext";
import { getAuditFields } from "../../../shared/utils/auditFields";
import { type BiologicalTreatmentAssignmentsPayload } from "../utils/biologicalAssignments";

export interface SaveBiologicalAssignmentsVariables {
  trialId: string;
  payload: BiologicalTreatmentAssignmentsPayload;
  standardFieldProperties?: Record<string, unknown>;
}

const useSaveBiologicalAssignments = (): UseMutationResult<
  unknown,
  unknown,
  SaveBiologicalAssignmentsVariables
> => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation<unknown, unknown, SaveBiologicalAssignmentsVariables>({
    mutationFn: async ({
      trialId,
      payload,
      standardFieldProperties,
    }: SaveBiologicalAssignmentsVariables): Promise<unknown> => {
      if (standardFieldProperties) {
        await AxiosInstance.patch(`/trial/${trialId}`, {
          standard_field_properties: standardFieldProperties,
          ...getAuditFields("patch", user?.id),
        });
      }
      const response = await AxiosInstance.patch(
        `/trial/${trialId}/plot-property-assignments`,
        payload
      );
      return response.data;
    },
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["trial", variables.trialId],
      });
      void queryClient.invalidateQueries({ queryKey: ["trialInsights"] });
    },
  });
};

export default useSaveBiologicalAssignments;
